import { useEffect, useState } from 'react';
import { Label } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { Domain, AiFeature } from '@tiendanube/common/src/enums';
import { Input, InterfaceNameValue, Stack } from '@tiendanube/components';
import useLayoutScroll from 'App/components/AppLayout/useLayoutScroll';
import { FEATURE_AI_TIER } from 'App/features';
import {
  CATALOG_EXTENDED_CATEGORY_DESCRIPTION,
  AI_CATEGORIES_DESCRIPTION_ENABLED,
} from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { DEFAULT_LANGUAGE, LanguageInterface, LanguagesType } from 'App/i18n';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import { useModal } from 'commons/hooks';
import { useAiUsage } from 'domains/Ai/hooks';
import { trackingAiProductDescriptionEdit } from 'domains/Ai/tracking';
import { useGetAllLanguages } from 'domains/Auth/hooks';
import useGetTags from 'domains/Auth/hooks/useGetTags';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import AICategoryDescriptionGeneratorModal from '../../../AICategoryDescriptionGeneratorModal';

interface NameValuesi18n {
  name: string;
  value: LanguageInterface;
}
export interface FormNameAndDescriptionProps {
  id: string;
  name: LanguageInterface;
  description: LanguageInterface;
  onChange: (data: NameValuesi18n) => void;
  language?: LanguagesType;
  errors: Partial<Record<string, string>>;
}

const EXTENDED_LENGTH = 5000;
const STANDARD_LENGTH = 140;

const EXTENDED_LENGTH_ROWS = 12;
const STANDARD_LENGTH_ROWS = 3;

function FormNameAndDescription({
  id,
  name,
  errors,
  description,
  onChange,
  language = DEFAULT_LANGUAGE,
}: FormNameAndDescriptionProps): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const { scrollToError } = useLayoutScroll();

  const tags = useGetTags();
  const allLanguages = useGetAllLanguages();
  const { categoryDescriptionLength, categoryDescriptionRows } =
    setParametersForDescriptionLength(tags);

  const [hasAiCategoryDescriptionEnabled] = useHasTags([
    AI_CATEGORIES_DESCRIPTION_ENABLED,
  ]);

  // Track if description was generated with AI per language
  const [isDescriptionGeneratedAI, setIsDescriptionGeneratedAI] = useState<
    Record<string, boolean>
  >(
    allLanguages.reduce(
      (acc, lang) => ({
        ...acc,
        [lang]: false,
      }),
      {},
    ),
  );

  const hasError = !!errors[`name.${language}`];

  const [
    showCategoryDescriptionGeneratorModal,
    openCategoryDescriptionGeneratorModal,
    closeCategoryDescriptionGeneratorModal,
  ] = useModal();

  const { isLoadingAiUses, requiredTier, aiUsesUpsellTitle } = useAiUsage();

  const handleOpenCategoryDescriptionGeneratorModal = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_CATEGORIES_DESCRIPTION,
    callback: openCategoryDescriptionGeneratorModal,
    minValue: requiredTier,
  });

  const handleChangeName = (data: InterfaceNameValue) => {
    onChange({
      name: data.name,
      value: { ...name, [language]: data.value },
    });
  };

  const handleChangeDescription = (data: InterfaceNameValue) => {
    onChange({
      name: data.name,
      value: { ...description, [language]: data.value },
    });

    // Track if user manually edited AI-generated description
    if (isDescriptionGeneratedAI[language]) {
      trackingAiProductDescriptionEdit(
        {
          feature: AiFeature.AI_CATEGORIES_DESCRIPTION,
        },
        language,
      );
      setIsDescriptionGeneratedAI((prev) => ({ ...prev, [language]: false }));
    }
  };

  const handleUseDescription = (generatedDescription: string) => {
    onChange({
      name: 'description',
      value: { ...description, [language]: generatedDescription },
    });
    // Mark description as AI-generated for current language
    setIsDescriptionGeneratedAI((prev) => ({ ...prev, [language]: true }));
    closeCategoryDescriptionGeneratorModal();
  };

  const errorName = errors[`name.${language}`];
  const appearanceName = errorName ? 'validation_error' : undefined;
  const helpTextName = errorName ? t(errorName) : '';

  const errorDescription = errors[`description.${language}`];

  const appearanceDescription = errorDescription
    ? 'validation_error'
    : undefined;

  const helperTextDescription = errorDescription
    ? t(errorDescription)
    : t('categories.detail.description.helperText', {
        count: description[language]?.length || 0,
        max: categoryDescriptionLength,
      });

  useEffect(() => {
    if (hasError) scrollToError();
  }, [hasError, scrollToError]);

  return (
    <>
      <AICategoryDescriptionGeneratorModal
        show={showCategoryDescriptionGeneratorModal}
        category={{
          id,
          name: name[language],
          lang: language,
        }}
        onUseDescription={handleUseDescription}
        onDismiss={closeCategoryDescriptionGeneratorModal}
      />
      <Stack column align="stretch" spacing="tight">
        <Stack.Item>
          <Input
            type="text"
            label={t('categories.detail.name.label')}
            placeholder={t('categories.detail.name.placeholder')}
            onChange={handleChangeName}
            name="name"
            value={name[language]}
            appearance={appearanceName}
            helpText={helpTextName}
          />
        </Stack.Item>
        <Stack.Item>
          <Stack column align="stretch" spacing="tight">
            <Stack spacing="tight">
              <Stack.Item>
                <Label>{t('categories.detail.description.label')}</Label>
              </Stack.Item>
              {!isLoadingAiUses && hasAiCategoryDescriptionEnabled && (
                <Stack.Item>
                  <GenerateWithAiLink
                    trackingFeature={AiFeature.AI_CATEGORIES_DESCRIPTION}
                    onClick={handleOpenCategoryDescriptionGeneratorModal}
                    fontSize="caption"
                  />
                </Stack.Item>
              )}
            </Stack>
            <Input
              type="text"
              placeholder={t('categories.detail.description.placeholder')}
              onChange={handleChangeDescription}
              name="description"
              value={description[language]}
              rows={categoryDescriptionRows}
              multiRows
              maxLength={categoryDescriptionLength}
              appearance={appearanceDescription}
              helpText={helperTextDescription}
            />
          </Stack>
        </Stack.Item>
      </Stack>
    </>
  );
}

export default FormNameAndDescription;

function isDescriptionExtended(tags: string[]): boolean {
  return tags.includes(CATALOG_EXTENDED_CATEGORY_DESCRIPTION);
}

function setParametersForDescriptionLength(tags: string[]) {
  const isExtendedDescription = isDescriptionExtended(tags);
  const categoryDescriptionLength = isExtendedDescription
    ? EXTENDED_LENGTH
    : STANDARD_LENGTH;

  const categoryDescriptionRows = isExtendedDescription
    ? EXTENDED_LENGTH_ROWS
    : STANDARD_LENGTH_ROWS;

  return {
    categoryDescriptionLength,
    categoryDescriptionRows,
  };
}
